import React, { useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../ui/table';
import { Star, Download, Copy, MessageSquareText, Trash2, QrCode, Users } from 'lucide-react';
import { toast } from 'sonner';

const Stars = ({ value }) => (
  <div className="flex items-center gap-0.5" title={`${value} / 5`}>
    {[1, 2, 3, 4, 5].map((s) => (
      <Star key={s} className={`w-3.5 h-3.5 ${s <= value ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`} />
    ))}
  </div>
);

// One QR code for every centre — it links to the plain feedback form (no
// venue pre-filled) and the visitor picks their centre from the dropdown
// there. Simpler to print/distribute than a code per centre.
const SITE_VISIT_FEEDBACK_URL = `${window.location.origin}/site-visit-feedback`;

const SiteVisitQRCard = () => {
  const [dataUrl, setDataUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(SITE_VISIT_FEEDBACK_URL, { width: 280, margin: 1 }).then((url) => {
      if (!cancelled) setDataUrl(url);
    });
    return () => { cancelled = true; };
  }, []);

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = 'clayworks-site-visit-feedback-qr.png';
    a.click();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(SITE_VISIT_FEEDBACK_URL);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  return (
    <Card className="max-w-xs">
      <CardContent className="p-6 flex flex-col items-center gap-3 text-center">
        <div className="font-medium text-sm">Site Visit Feedback</div>
        {dataUrl ? (
          <img src={dataUrl} alt="QR code for site visit feedback form" className="w-48 h-48" data-testid="mo-site-visit-qr" />
        ) : (
          <div className="w-48 h-48 bg-muted animate-pulse rounded" />
        )}
        <p className="text-xs text-muted-foreground">Same code for every centre — visitors pick their centre in the form.</p>
        <div className="flex gap-2 w-full">
          <Button size="sm" variant="outline" className="flex-1" onClick={download} disabled={!dataUrl}>
            <Download className="w-3.5 h-3.5 mr-1" /> Download
          </Button>
          <Button size="sm" variant="outline" className="flex-1" onClick={copyLink}>
            <Copy className="w-3.5 h-3.5 mr-1" /> Copy Link
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const SiteVisitFeedbackTab = ({ api }) => {
  const [venues, setVenues] = useState([]);
  const [responses, setResponses] = useState([]);
  const [summary, setSummary] = useState({ total_responses: 0, avg_rating: 0, by_venue: [] });
  const [loading, setLoading] = useState(true);
  const [showQrPanel, setShowQrPanel] = useState(false);
  const [venueFilter, setVenueFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = venueFilter !== 'all' ? { venue_id: venueFilter } : {};
      const [venuesRes, responsesRes, summaryRes] = await Promise.all([
        api.get('/venues'),
        api.get('/site-visit-feedback/responses', { params }),
        api.get('/site-visit-feedback/summary').catch(() => ({ data: null })),
      ]);
      setVenues(venuesRes.data || []);
      setResponses(responsesRes.data.responses || []);
      if (summaryRes.data) setSummary(summaryRes.data);
    } catch (err) {
      toast.error('Failed to load site visit feedback');
    } finally {
      setLoading(false);
    }
  }, [api, venueFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const deleteResponse = async (response) => {
    if (!window.confirm(`Delete this feedback response from ${response.venue_name}?`)) return;
    try {
      await api.delete(`/site-visit-feedback/responses/${response.id}`);
      toast.success('Response deleted');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete response');
    }
  };

  if (loading) {
    return <div className="py-12 text-center text-muted-foreground">Loading site visit feedback…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <Users className="w-5 h-5 text-primary" />
          <div><div className="text-xs text-muted-foreground">Total Responses</div><div className="text-xl font-bold">{summary.total_responses}</div></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
          <div><div className="text-xs text-muted-foreground">Average Rating</div><div className="text-xl font-bold">{summary.avg_rating || '—'}</div></div>
        </CardContent></Card>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-lg font-semibold">Site Visit Feedback</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={venueFilter} onValueChange={setVenueFilter}>
            <SelectTrigger className="w-56"><SelectValue placeholder="All Centres" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Centres</SelectItem>
              {venues.map((v) => (
                <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => setShowQrPanel((s) => !s)} data-testid="mo-toggle-qr-panel">
            <QrCode className="w-4 h-4 mr-1.5" /> {showQrPanel ? 'Hide QR Code' : 'QR Code'}
          </Button>
        </div>
      </div>

      {showQrPanel && (
        <div>
          <p className="text-sm text-muted-foreground mb-3">
            Print this once and place it at every centre where visitors check out.
          </p>
          <SiteVisitQRCard />
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Centre</TableHead>
                <TableHead>Accompanied By</TableHead>
                <TableHead>Broker</TableHead>
                <TableHead>Rating</TableHead>
                <TableHead>Feedback</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {responses.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No site visit feedback yet</TableCell></TableRow>
              ) : responses.map((r) => (
                <TableRow key={r.id} data-testid={`mo-site-visit-row-${r.id}`}>
                  <TableCell className="whitespace-nowrap text-sm">{new Date(r.submitted_at).toLocaleDateString()}</TableCell>
                  <TableCell className="font-medium">{r.venue_name}</TableCell>
                  <TableCell>{r.accompanied_by_name}</TableCell>
                  <TableCell className="text-muted-foreground">{r.broker_name || '—'}</TableCell>
                  <TableCell><Stars value={r.overall_rating} /></TableCell>
                  <TableCell className="max-w-xs">
                    {(r.experience_comments || r.improvement_suggestions) ? (
                      <div className="flex items-start gap-1.5 text-sm text-muted-foreground">
                        <MessageSquareText className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <div className="space-y-1">
                          {r.experience_comments && <p className="line-clamp-2">{r.experience_comments}</p>}
                          {r.improvement_suggestions && <p className="line-clamp-2 italic">Suggestion: {r.improvement_suggestions}</p>}
                        </div>
                      </div>
                    ) : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => deleteResponse(r)} aria-label="Delete response">
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
